import { ILCHttpResponse, IUser } from '@logicalcommander/types'
import { createApi } from '@reduxjs/toolkit/query/react'
import { v4 as uuidv4 } from 'uuid'
import { configManager } from '../../config/configManager'
import { ICreateUserResponse } from '../../interfaces/user.interfaces'
import { RootState } from '../redux/store'
import { eUsersSearchFor } from '../ui/components/lc-search-user/lc-search-user'
import { createBaseQueryWithErrorHandling } from './baseQueryWithErrorHandling'

export interface IRevokeAdminAccessDTO {
  identity: string
}

export interface IUpdateMfaUser {
  identity: string
  mfaEnabled: boolean
}

export interface IUpdateUserDTO {
  user: Partial<IUser>
  identity: string
  /** Current UI locale for pending email verification template when email changes */
  language?: string
}

export interface ICheckDuplicateMobileRequest {
  mobile: string
  excludeIdentity?: string
}

export interface IDuplicateMobileMatch {
  identity: string
  fullName: string
}

export interface IUpdateAdminAccessDTO {
  identity: string
  role: string
}

export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: createBaseQueryWithErrorHandling(`${configManager.api.GATEWAY_URL}/user`, (headers, { getState }) => {
    headers.set('x-request-id', uuidv4())
    const { token, companyToken } = (getState() as RootState).authReducer
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    if (companyToken) {
      headers.set('x-company-token', companyToken)
    }
    return headers
  }),
  tagTypes: ['ProfileImage'],
  endpoints: (builder) => ({
    createUser: builder.mutation<ICreateUserResponse, Partial<IUser>>({
      query: (user) => ({
        url: '',
        method: 'POST',
        body: {
          ...user,
        },
      }),
      transformResponse: (rawResult: ILCHttpResponse<ICreateUserResponse>, meta) => {
        return rawResult.data as ICreateUserResponse
      },
    }),
    updateUser: builder.mutation<ICreateUserResponse, IUpdateUserDTO>({
      query: ({ user, identity, language }) => ({
        url: `/${identity}`,
        method: 'PUT',
        body: {
          ...user,
          ...(language ? { language } : {}),
        },
      }),
      transformResponse: (rawResult: ILCHttpResponse<ICreateUserResponse>, meta) => {
        return rawResult.data as ICreateUserResponse
      },
    }),
    deleteUser: builder.mutation<any, string>({
      query: (identity) => ({
        url: `/${identity}`,
        method: 'DELETE',
      }),
      transformResponse: (rawResult: ILCHttpResponse<any>, meta) => {
        return rawResult.data
      },
    }),
    toogleMfa: builder.mutation<IUser[], IUpdateMfaUser[]>({
      query: (updateMfaUsers) => ({
        url: `/mfa`,
        method: 'PUT',
        body: {
          items: updateMfaUsers,
        },
      }),
      transformResponse: (rawResult: ILCHttpResponse<IUser[]>, meta) => {
        return rawResult.data as IUser[]
      },
    }),
    archiveUsers: builder.mutation<IUser[], { identities: string[] }>({
      query: (archiveUsersDto) => ({
        url: `/archive`,
        method: 'PUT',
        body: {
          identities: archiveUsersDto.identities,
        },
      }),
      transformResponse: (rawResult: ILCHttpResponse<IUser[]>, meta) => {
        return rawResult.data as IUser[]
      },
    }),
    restoreUsers: builder.mutation<IUser[], { identities: string[] }>({
      query: (restoreUsersDto) => ({
        url: `/restore`,
        method: 'PUT',
        body: {
          identities: restoreUsersDto.identities,
        },
      }),
      transformResponse: (rawResult: ILCHttpResponse<IUser[]>, meta) => {
        return rawResult.data as IUser[]
      },
    }),
    deleteArchivedUsers: builder.mutation<IUser[], { identities: string[] }>({
      query: (deleteUsersDto) => ({
        url: `/archive/delete`,
        method: 'DELETE',
        body: {
          identities: deleteUsersDto.identities,
        },
      }),
      transformResponse: (rawResult: ILCHttpResponse<IUser[]>, meta) => {
        return rawResult.data as IUser[]
      },
    }),
    getUser: builder.query<IUser, string>({
      query: (identity) => ({
        url: `/${identity}`,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IUser>, meta) => {
        return rawResult.data as IUser
      },
    }),
    uploadTmpProfileImage: builder.mutation<{ url: string; tmpPath: string }, FormData>({
      query: (body) => ({
        url: '/tmp/profile-image',
        method: 'POST',
        body,
      }),
      transformResponse: (rawResult: ILCHttpResponse<any>, meta) => {
        return rawResult.data
      },
    }),
    deleteProfileImage: builder.mutation<any, string>({
      query: (identity) => ({
        url: `/profile-image/${identity}`,
        method: 'DELETE',
      }),
      transformResponse: (rawResult: ILCHttpResponse<any>, meta) => {
        return rawResult.data
      },
      // Invalidate the profile image cache to reflect the deletion in Redux
      // This ensures that all components using getOthersProfileImageQuery will update
      // when the image is deleted, similar to how the update employee button works
      invalidatesTags: (result, error, identity) => [{ type: 'ProfileImage', id: identity }],
    }),
    getOthersProfileImage: builder.query<{ url: string } | null, string>({
      query: (identity) => ({
        url: `/tmp-url/profile-image/${identity}`,
      }),
      keepUnusedDataFor: 60 * 20, // 20 min
      transformResponse: (rawResult: ILCHttpResponse<{ url: string } | null>, meta) => {
        return rawResult.data || null
      },
      providesTags: (result, error, identity) => [{ type: 'ProfileImage', id: identity }],
    }),
    uploadOthersProfileImage: builder.mutation<any, { body: FormData; identity: string }>({
      query: ({ body, identity }) => ({
        url: `/profile-image/${identity}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (rawResult: ILCHttpResponse<any>, meta) => {
        return rawResult.data
      },
      invalidatesTags: (result, error, { identity }) => [{ type: 'ProfileImage', id: identity }],
    }),
    revokeAdminAccess: builder.mutation<IUser, IRevokeAdminAccessDTO>({
      query: (body) => ({
        url: `/access/revoke`,
        method: 'PUT',
        body,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IUser>, meta) => {
        return rawResult.data as IUser
      },
    }),
    updateAdminAccess: builder.mutation<IUser, IUpdateAdminAccessDTO>({
      query: (body) => ({
        url: `/access/update`,
        method: 'PUT',
        body,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IUser>, meta) => {
        return rawResult.data as IUser
      },
    }),
    searchUser: builder.mutation<IUser[], { nameOrIdentity: string; searchFor: eUsersSearchFor }>({
      query: ({ nameOrIdentity, searchFor }) => ({
        url: `/search/${nameOrIdentity}`,
        method: 'POST',
        body: { searchFor },
      }),
      transformResponse: (rawResult: ILCHttpResponse<IUser[]>, meta) => {
        return rawResult.data as IUser[]
      },
    }),
    inviteAdmin: builder.mutation<IUser, { role: string; identity: string }>({
      query: (body) => ({
        url: `/access/invite`,
        method: 'POST',
        body,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IUser>, meta) => {
        return rawResult.data as IUser
      },
    }),
    checkDuplicateMobile: builder.mutation<IDuplicateMobileMatch[], ICheckDuplicateMobileRequest>({
      query: (body) => ({
        url: '/check-duplicate-mobile',
        method: 'POST',
        body,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IDuplicateMobileMatch[]>) => rawResult.data ?? [],
    }),
  }),
})

export const {
  useCreateUserMutation,
  useArchiveUsersMutation,
  useRestoreUsersMutation,
  useDeleteArchivedUsersMutation,
  useLazyGetUserQuery,
  useUpdateUserMutation,
  useUploadTmpProfileImageMutation,
  useDeleteProfileImageMutation,
  useGetOthersProfileImageQuery,
  useUploadOthersProfileImageMutation,
  useRevokeAdminAccessMutation,
  useUpdateAdminAccessMutation,
  useSearchUserMutation,
  useInviteAdminMutation,
  useToogleMfaMutation,
  useCheckDuplicateMobileMutation,
} = userApi
